const BASE_URL = 'https://server.investain.com/crm/api';

// Generic function to make API requests
export const makeApiRequest = async (endpoint, accessToken, method = 'GET', body = null) => {
    try {
        const options = {
            method,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'x-api-key': process.env.REACT_APP_API_KEY,
            },
        };

        // Add Authorization header only if accessToken is provided
        if (accessToken) {
            options.headers.Authorization = `Bearer ${accessToken}`;
        }

        if (body) {
            options.body = JSON.stringify(body);
        }

        const response = await fetch(`${BASE_URL}${endpoint}`, options);

        // If the response is not OK, throw an error with the status code
        if (!response.ok) {
            const errorData = await response.json(); // Parse error response if available
            throw {
                status: response.status,
                message: errorData.message || 'An error occurred while processing your request.',
            };
        }

        const data = await response.json();
        return { error: false, data };
    } catch (error) {
        // Return detailed error information
        return {
            error: true,
            status: error.status || 500, // Default to 500 if no status is provided
            message: error.message || 'An unexpected error occurred. Please try again later.',
        };
    }
};

// Fetch financial data
export const fetchFinancialData = async (accessToken, userId) => {
    return makeApiRequest(`/tradingAccount/${userId}/get`, accessToken);
};

// Fetch recent activity data
export const fetchRecentActivity = async (accessToken, userId) => {
    return makeApiRequest(`/recentActivity/${userId}/get`, accessToken);
};

// Fetch wallet data
export const fetchWalletData = async (accessToken, userId) => {
    return makeApiRequest(`/wallet/${userId}/get`, accessToken);
};

// Fetch open positions for a trading account (Portal Backend)
// GET /internal/positions/account/:accountId
// `accountId` must be a valid UUID. Only positions with status "open" are
// returned; an account with no open positions returns count 0 with an empty
// array. The portal access token (Bearer) and x-api-key are injected by
// makeApiRequest, matching every other portal route.
export const fetchOpenPositions = async (accessToken, accountId) => {
    return makeApiRequest(`/internal/positions/account/${accountId}`, accessToken);
};

// Record a transaction
export const recordTransaction = async (accessToken, transactionData) => {
    return makeApiRequest('/transactionDetail/add', accessToken, 'POST', transactionData);
};

// Record recent activity
export const recordRecentActivity = async (accessToken, activityData) => {
    return makeApiRequest('/recentActivity/add', accessToken, 'POST', activityData);
};

// Send withdrawal email
export const sendWithdrawalEmail = async (accessToken, withdrawalData) => {
    return makeApiRequest('/withDrawalEmail', accessToken, 'POST', withdrawalData);
};

// Add demo account
export const addNewDemoAccount = async (demoAccountData, accessToken) => {
    return makeApiRequest('/tradingAccount/add', null, 'POST', demoAccountData);
};

// Send reset password email
export const sendResetPasswordEmail = async (email) => {
    const response = await makeApiRequest('/user/forget_password', null, 'POST', { email });

    if (response.error) {
        // Handle specific status codes
        switch (response.status) {
            case 404:
                return { error: true, message: 'Email not found. Please check your email address and try again.' };
            case 400:
                return { error: true, message: 'Invalid request. Please ensure your email is correctly formatted.' };
            case 500:
                return { error: true, message: 'A server error occurred. Please try again later.' };
            default:
                return { error: true, message: response.message };
        }
    }

    // If no error, return success
    return { error: false, message: 'A password reset link has been sent to your email.' };
};

// Reset password with token
export const resetPassword = async (token, newPassword) => {
    return makeApiRequest('/user/reset_password', null, 'POST', { token, newPassword });
};

export const fetchSubusers = async (accessToken, userId) => {
    const response = await makeApiRequest(`/user/${userId}/getProfile`, accessToken);
    if (!response.error && response.data.status === 200) {
        const subusers = response.data.user?.subusers ? JSON.parse(response.data.user.subusers) : [];
        return { error: false, data: subusers };
    } else {
        return { error: true, message: response.message || 'Failed to fetch subusers.' };
    }
};

// Fetch single user details by ID
export const fetchUserDetails = async (accessToken, userId) => {
    const response = await makeApiRequest(`/user/${userId}/getProfile`, accessToken);
    if (!response.error && response.data.status === 200) {
        return { error: false, data: response.data.user };
    } else {
        return { error: true, message: response.message || 'Failed to fetch user details.' };
    }
};
